import { For, Show, createSignal, onMount } from "solid-js";
import { STATUS_LABELS, formatDay, type AdminApi, type NewSchool, type Paged, type SchoolRow } from "./api";
import { SchoolDetailView } from "./SchoolDetailView";

const SCHOOL_TYPES = ["Grundschule", "Hauptschule", "Realschule", "Sekundarschule", "Gesamtschule", "Gymnasium", "Förderschule", "Berufskolleg", "Andere"];

export function endLabel(school: SchoolRow) {
  if (school.status === "suspended" || school.status === "deleted") return "–";
  if (school.licenseKind === "manual") return school.licenseEndsAt ? `Lizenz bis ${formatDay(school.licenseEndsAt)}` : "Lizenz ohne Ende";
  return school.trialEndsAt ? `Test bis ${formatDay(school.trialEndsAt)}` : "–";
}

export function SchoolsView(props: { api: AdminApi; guard: <T>(work: () => Promise<T>) => Promise<T> }) {
  const [data, setData] = createSignal<Paged<SchoolRow>>({ items: [], total: 0, page: 1, limit: 25 });
  const [search, setSearch] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [selected, setSelected] = createSignal<string | null>(null);
  const [creating, setCreating] = createSignal(false);
  const [createError, setCreateError] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [mode, setMode] = createSignal<"trial" | "manual">("trial");

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      setData(await props.guard(() => props.api.schools(page, search())));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  onMount(() => void load());

  const create = async (event: SubmitEvent) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget as HTMLFormElement);
    const field = (name: string) => String(values.get(name) ?? "").trim();
    const input: NewSchool = {
      name: field("name"), schoolType: field("schoolType"), schoolNumber: field("schoolNumber"), street: field("street"),
      postalCode: field("postalCode"), city: field("city"), estClasses: field("estClasses"),
      mode: mode(), licenseEndsOn: field("licenseEndsOn"), reason: field("reason")
    };
    setSaving(true);
    setCreateError("");
    try {
      const created = await props.guard(() => props.api.createSchool(input));
      setCreating(false);
      setSelected(created.id);
    } catch (cause) {
      setCreateError(cause instanceof Error ? cause.message : "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const pages = () => Math.max(1, Math.ceil(data().total / data().limit));

  return (
    <Show when={selected()} fallback={
      <section>
        <div class="adm-head">
          <h2>Schulen</h2>
          <button class="adm-btn small" type="button" onClick={() => { setCreating(!creating()); setCreateError(""); }}>{creating() ? "Abbrechen" : "Neue Schule"}</button>
        </div>

        <Show when={creating()}>
          <form class="adm-card adm-dialog" onSubmit={create}>
            <h3>Schule anlegen</h3>
            <p class="adm-sub">Für Pilotschulen und Bestandsschulen. Lehrkräfte ordnest du danach in der Schule zu.</p>
            <div class="adm-grid2">
              <label class="adm-field wide"><span>Name der Schule</span><input name="name" class="adm-input" required minlength={2} maxlength={200} /></label>
              <label class="adm-field"><span>Schulform</span>
                <select name="schoolType" class="adm-input"><option value="">–</option><For each={SCHOOL_TYPES}>{(type) => <option value={type}>{type}</option>}</For></select></label>
              <label class="adm-field"><span>Schulnummer (6 Ziffern, optional)</span><input name="schoolNumber" class="adm-input" inputmode="numeric" pattern="\d{6}" maxlength={6} /></label>
              <label class="adm-field"><span>Straße und Hausnummer</span><input name="street" class="adm-input" maxlength={200} /></label>
              <label class="adm-field"><span>PLZ</span><input name="postalCode" class="adm-input" inputmode="numeric" pattern="\d{5}" maxlength={5} /></label>
              <label class="adm-field"><span>Ort</span><input name="city" class="adm-input" maxlength={100} /></label>
              <label class="adm-field"><span>Ungefähre Klassenzahl (optional)</span><input name="estClasses" class="adm-input" inputmode="numeric" maxlength={3} /></label>
              <label class="adm-field"><span>Start</span>
                <select class="adm-input" value={mode()} onChange={(event) => setMode(event.currentTarget.value as "trial" | "manual")}>
                  <option value="trial">28 Tage Test (bis 3 Klassen)</option>
                  <option value="manual">Manuelle Lizenz, ohne Rechnung</option>
                </select></label>
              <Show when={mode() === "manual"}>
                <label class="adm-field"><span>Lizenz-Ende (leer = unbefristet)</span><input name="licenseEndsOn" class="adm-input" type="date" /></label>
              </Show>
              <label class="adm-field wide"><span>Grund (mindestens 5 Zeichen, steht im Protokoll)</span><textarea name="reason" class="adm-input" required minlength={5} maxlength={500} /></label>
            </div>
            <div class="adm-bar"><button class="adm-btn" type="submit" disabled={saving()}>Schule anlegen</button></div>
            <Show when={createError()}><p class="adm-error" role="alert">{createError()}</p></Show>
          </form>
        </Show>

        <form class="adm-bar" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
          <input class="adm-input" type="search" placeholder="Suchen nach Name, Ort oder Schulnummer" aria-label="Schulen suchen" value={search()} onInput={(event) => setSearch(event.currentTarget.value)} />
          <button class="adm-btn ghost" type="submit">Suchen</button>
        </form>
        <Show when={error()}><p class="adm-error" role="alert">{error()}</p></Show>
        <div class="adm-card">
          <div class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Schule</th><th>Ort</th><th>Status</th><th>Laufzeit</th><th>Lehrkräfte</th><th>Klassen</th></tr></thead>
              <tbody>
                <For each={data().items}>
                  {(school) => (
                    <tr>
                      <td><button class="adm-link" type="button" onClick={() => setSelected(school.id)}>{school.name}</button></td>
                      <td>{school.city ?? "–"}</td>
                      <td><span class={`adm-pill ${school.status}`}>{STATUS_LABELS[school.status] ?? school.status}</span></td>
                      <td>{endLabel(school)}</td>
                      <td class="num">{school.memberCount ?? 0}</td>
                      <td class="num">{school.classCount ?? 0}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <Show when={!loading() && data().items.length === 0}><p class="adm-empty">Noch keine Schulen angelegt.</p></Show>
          <div class="adm-pager">
            <button class="adm-btn ghost small" type="button" disabled={data().page <= 1 || loading()} onClick={() => void load(data().page - 1)}>Zurück</button>
            <span>Seite {data().page} von {pages()} · {data().total} Schulen</span>
            <button class="adm-btn ghost small" type="button" disabled={data().page >= pages() || loading()} onClick={() => void load(data().page + 1)}>Weiter</button>
          </div>
        </div>
      </section>
    }>
      {(id) => <SchoolDetailView api={props.api} guard={props.guard} schoolId={id()} onBack={() => { setSelected(null); void load(data().page); }} />}
    </Show>
  );
}
